/**
 * Authentication Service
 * API calls for authentication operations
 */

import apiClient, { tokenManager } from './api.client';
import type {
  ApiResponse,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  LoginResult,
  TwoFactorRequiredResponse,
  User,
  TrustedDevice,
} from '@/types/api.types';

/**
 * Check if a login result requires 2FA
 */
export const isTwoFactorRequired = (result: LoginResult): result is TwoFactorRequiredResponse => {
  return 'requiresTwoFactor' in result && result.requiresTwoFactor === true;
};

/**
 * Login user — may return tokens or require 2FA
 */
export const login = async (credentials: LoginRequest): Promise<LoginResult> => {
  const response = await apiClient.post<ApiResponse<LoginResult>>(
    '/auth/login',
    credentials,
    { withCredentials: true } // Send/receive cookies for trusted device + refresh token
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Login failed');
  }

  const result = response.data.data;

  // If 2FA not required, store the access token (refresh token is in the
  // httpOnly 'rt' cookie set by the server — audit #17).
  if (!isTwoFactorRequired(result)) {
    const { tokens } = result;
    tokenManager.setAccessToken(tokens.accessToken);
  }

  return result;
};

/**
 * Verify 2FA code and complete login
 */
export const verifyTwoFactor = async (twoFactorToken: string, code: string): Promise<AuthResponse> => {
  const response = await apiClient.post<ApiResponse<AuthResponse>>(
    '/auth/verify-2fa',
    { twoFactorToken, code },
    { withCredentials: true } // Receive trusted device + refresh token cookies
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Verification failed');
  }

  const { user, tokens } = response.data.data;

  // Store the access token (refresh token is in the httpOnly 'rt' cookie).
  tokenManager.setAccessToken(tokens.accessToken);

  return { user, tokens };
};

/**
 * Resend 2FA code
 */
export const resendTwoFactor = async (twoFactorToken: string): Promise<void> => {
  const response = await apiClient.post<ApiResponse<{ message: string }>>(
    '/auth/resend-2fa',
    { twoFactorToken }
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to resend code');
  }
};

/**
 * Register new user
 */
export const register = async (userData: RegisterRequest): Promise<AuthResponse> => {
  const response = await apiClient.post<ApiResponse<AuthResponse>>(
    '/auth/register',
    userData
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Registration failed');
  }

  const { user, tokens } = response.data.data;

  // Store the access token (refresh token is in the httpOnly 'rt' cookie).
  tokenManager.setAccessToken(tokens.accessToken);

  return { user, tokens };
};

/**
 * Logout user
 */
export const logout = async (): Promise<void> => {
  try {
    await apiClient.post('/auth/logout', undefined, { withCredentials: true });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    tokenManager.clearTokens();
  }
};

/**
 * Get current user profile
 */
export const getCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get<ApiResponse<{ user: User }>>('/auth/me');

  if (!response.data.data) {
    throw new Error('Failed to get user profile');
  }

  return response.data.data.user;
};

/**
 * Refresh access token
 *
 * The refresh token is sent automatically via the httpOnly 'rt' cookie
 * (audit #17); nothing is read from or sent in client-side JS storage.
 */
export const refreshAccessToken = async (): Promise<{ accessToken: string }> => {
  const response = await apiClient.post<ApiResponse<{ accessToken: string }>>(
    '/auth/refresh',
    {},
    { withCredentials: true }
  );

  if (!response.data.data) {
    throw new Error('Failed to refresh token');
  }

  const { accessToken } = response.data.data;

  // Update the access token
  tokenManager.setAccessToken(accessToken);

  return { accessToken };
};

/**
 * Change the current user's password
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  await apiClient.patch<ApiResponse<{ message: string }>>('/auth/password', {
    currentPassword,
    newPassword,
  });
};

/**
 * Request a password reset email (always returns a generic success message)
 */
export const forgotPassword = async (email: string): Promise<void> => {
  await apiClient.post<ApiResponse<{ message: string }>>('/auth/forgot-password', {
    email,
  });
};

/**
 * Reset password using a token from the reset email
 */
export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<void> => {
  await apiClient.post<ApiResponse<{ message: string }>>('/auth/reset-password', {
    token,
    newPassword,
  });
};

/**
 * Get user's trusted devices
 */
export const getDevices = async (): Promise<TrustedDevice[]> => {
  const response = await apiClient.get<ApiResponse<{ devices: TrustedDevice[] }>>(
    '/auth/devices',
    { withCredentials: true }
  );

  if (!response.data.data) {
    throw new Error('Failed to fetch devices');
  }

  return response.data.data.devices;
};

/**
 * Revoke a specific trusted device
 */
export const revokeDevice = async (deviceId: string): Promise<void> => {
  await apiClient.delete(`/auth/devices/${deviceId}`, { withCredentials: true });
};

/**
 * Revoke all other trusted devices
 */
export const revokeAllOtherDevices = async (): Promise<number> => {
  const response = await apiClient.delete<ApiResponse<{ count: number; message: string }>>(
    '/auth/devices',
    { withCredentials: true }
  );

  return response.data.data?.count || 0;
};
